import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { gamesFiltersModalActions } from "../../store/gamesFiltersModalSlice";
import { useEffect, useState } from "react";
import Select from "react-select";
import { useGetMunicipalitiesQuery } from "../../store/services/municipalitiesServices";
import { useGetNACCodesQuery } from "../../store/services/nacServices";
import { FiSearch, FiX } from "react-icons/fi";
import { LuListFilter } from "react-icons/lu";

const customStyles = {
    content: {
        position: "static",
        borderRadius: "20px",
        padding: 0,
        border: "none",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        maxWidth: "92vw",
    },
    overlay: {
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
    },
};

Modal.setAppElement(document.getElementById("root"));

const STATUS_OPTIONS = [
    { label: "Të gjitha", value: null },
    { label: "Aktive", value: "active" },
    { label: "E përfunduar", value: "ended" },
    { label: "E anuluar", value: "canceled" },
];

const GAME_TYPE_OPTIONS = [
    { label: "Të gjitha", value: null },
    { label: "Lotari", value: "lottery" },
    { label: "Cashback", value: "cashback" },
    { label: "Biznese", value: "business_draw" },
];

const labelClass = "text-xs font-semibold uppercase tracking-wider text-gray-500";
const inputClass =
    "w-full h-10 px-3.5 rounded-xl border border-gray-200 bg-white text-sm placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary transition";

const compactSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        borderRadius: 12,
        borderColor: state.isFocused ? "var(--secondary-color)" : "#e5e7eb",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(210,171,103,0.18)" : "none",
        fontSize: 13,
        "&:hover": { borderColor: "var(--secondary-color)" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    placeholder: (base) => ({ ...base, fontSize: 13, color: "#9ca3af" }),
    multiValue: (base) => ({ ...base, backgroundColor: "#f3f4f6", borderRadius: 6 }),
    multiValueLabel: (base) => ({ ...base, fontSize: 12, padding: "2px 6px" }),
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        backgroundColor: state.isSelected
            ? "var(--secondary-color)"
            : state.isFocused
            ? "rgba(210,171,103,0.1)"
            : "white",
        color: state.isSelected ? "white" : "#111827",
        cursor: "pointer",
    }),
    menu: (base) => ({ ...base, fontSize: 13, borderRadius: 10, overflow: "hidden" }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const toIsoDate = (d) => {
    if (!d) return "";
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

export default function GameFilters({ params, handleApplyAdvancedParams }) {
    const isOpen = useSelector((state) => state.game_filters_modal.isOpen);
    const dispatch = useDispatch();

    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedGameType, setSelectedGameType] = useState(null);
    const [selectedPrizeRange, setSelectedPrizeRange] = useState({ min: "", max: "" });
    const [selectedCashbackRange, setSelectedCashbackRange] = useState({ min: "", max: "" });
    const [selectedMunicipalities, setSelectedMunicipalities] = useState([]);
    const [selectedActivities, setSelectedActivities] = useState([]);
    const [selectedDateRange, setSelectedDateRange] = useState({ start_date: null, end_date: null });

    const { data: municipalities } = useGetMunicipalitiesQuery();
    const { data: activities } = useGetNACCodesQuery();

    useEffect(() => {
        if (!params) return;
        if (params.status) setSelectedStatus(params.status);
        if (params.type) setSelectedGameType(params.type);
        setSelectedPrizeRange({ min: params.min_value || "", max: params.max_value || "" });
        setSelectedCashbackRange({ min: params.start_cashback || "", max: params.end_cashback || "" });
        setSelectedDateRange({
            start_date: params.start_date ? new Date(params.start_date) : null,
            end_date: params.end_date ? new Date(params.end_date) : null,
        });
    }, [params]);

    const closeModal = () => dispatch(gamesFiltersModalActions.closeModal());

    const resetFilters = () => {
        setSelectedStatus(null);
        setSelectedGameType(null);
        setSelectedPrizeRange({ min: "", max: "" });
        setSelectedCashbackRange({ min: "", max: "" });
        setSelectedMunicipalities([]);
        setSelectedActivities([]);
        setSelectedDateRange({ start_date: null, end_date: null });
    };

    const applyFilters = () => {
        const next = {
            status: selectedStatus,
            type: selectedGameType,
            ...(selectedMunicipalities.length > 0
                ? { municipalities_no: selectedMunicipalities.map((v) => v.value).join(",") }
                : {}),
            ...(selectedActivities.length > 0
                ? { activities: selectedActivities.map((v) => v.value).join(",") }
                : {}),
            ...(selectedDateRange.start_date ? { start_date: toIsoDate(selectedDateRange.start_date) } : {}),
            ...(selectedDateRange.end_date ? { end_date: toIsoDate(selectedDateRange.end_date) } : {}),
            ...(selectedGameType === "cashback"
                ? { start_cashback: selectedCashbackRange.min || null, end_cashback: selectedCashbackRange.max || null }
                : {}),
            ...(selectedGameType === "lottery"
                ? { min_value: selectedPrizeRange.min || null, max_value: selectedPrizeRange.max || null }
                : {}),
            page: 1,
        };
        handleApplyAdvancedParams(next);
        closeModal();
    };

    const Chip = ({ active, onClick, children }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-3.5 h-9 rounded-full text-xs font-semibold transition border ${
                active
                    ? "bg-secondary text-white border-secondary shadow-sm"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
            }`}
        >
            {children}
        </button>
    );

    const RangeBlock = ({ label, value, onChange, suffix = "€" }) => (
        <div className="col-span-2 flex flex-col gap-1.5">
            <label className={labelClass}>{label}</label>
            <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                    <input
                        type="number"
                        className={`${inputClass} pr-9`}
                        value={value.min}
                        onChange={(e) => onChange({ ...value, min: e.target.value })}
                        placeholder="Nga"
                    />
                    <span className="absolute right-0 top-0 h-10 w-9 inline-flex items-center justify-center text-gray-400 text-xs border-l border-gray-200">
                        {suffix}
                    </span>
                </div>
                <div className="relative">
                    <input
                        type="number"
                        className={`${inputClass} pr-9`}
                        value={value.max}
                        onChange={(e) => onChange({ ...value, max: e.target.value })}
                        placeholder="Deri"
                    />
                    <span className="absolute right-0 top-0 h-10 w-9 inline-flex items-center justify-center text-gray-400 text-xs border-l border-gray-200">
                        {suffix}
                    </span>
                </div>
            </div>
        </div>
    );

    return (
        <Modal isOpen={isOpen} onRequestClose={closeModal} style={customStyles} contentLabel="Filterat e lojërave">
            <div className="bg-white w-[760px] max-w-[92vw] max-h-[88vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-secondary text-white">
                            <LuListFilter size={18} />
                        </span>
                        <div>
                            <div className="text-base font-semibold text-gray-900">Filterat</div>
                            <div className="text-xs text-gray-500">Përshtatni listën e lojërave sipas kriterëve</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={closeModal}
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition"
                        aria-label="Mbyll"
                    >
                        <FiX size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 scrollbar-thin scrollbar-thumb scrollbar-track">
                    {/* Status */}
                    <div className="flex flex-col gap-2">
                        <label className={labelClass}>Statusi i lojës</label>
                        <div className="flex flex-wrap gap-2">
                            {STATUS_OPTIONS.map((s) => (
                                <Chip key={String(s.value)} active={selectedStatus === s.value} onClick={() => setSelectedStatus(s.value)}>
                                    {s.label}
                                </Chip>
                            ))}
                        </div>
                    </div>

                    {/* Type */}
                    <div className="flex flex-col gap-2">
                        <label className={labelClass}>Tipi i lojës</label>
                        <div className="flex flex-wrap gap-2">
                            {GAME_TYPE_OPTIONS.map((t) => (
                                <Chip key={String(t.value)} active={selectedGameType === t.value} onClick={() => setSelectedGameType(t.value)}>
                                    {t.label}
                                </Chip>
                            ))}
                        </div>
                    </div>

                    {/* Range (conditional) */}
                    {selectedGameType === "lottery" && (
                        <div className="grid grid-cols-2 gap-4">
                            <RangeBlock label="Shuma e shpërblimit" value={selectedPrizeRange} onChange={setSelectedPrizeRange} suffix="€" />
                        </div>
                    )}
                    {selectedGameType === "cashback" && (
                        <div className="grid grid-cols-2 gap-4">
                            <RangeBlock label="Cashback përqindja" value={selectedCashbackRange} onChange={setSelectedCashbackRange} suffix="%" />
                        </div>
                    )}

                    {/* Region + Activities */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Komuna</label>
                            <Select
                                isMulti
                                name="municipality_no"
                                styles={compactSelectStyles}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                menuPosition="fixed"
                                options={municipalities?.data.map((m) => ({
                                    value: m.municipality_no,
                                    label: m.municipality_name,
                                }))}
                                placeholder="Të gjitha regjionet"
                                onChange={setSelectedMunicipalities}
                                value={selectedMunicipalities}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Aktivitetet</label>
                            <Select
                                isMulti
                                name="NAC_codes"
                                styles={compactSelectStyles}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                menuPosition="fixed"
                                options={activities?.data.map((o) => ({ value: o.id, label: o.name }))}
                                placeholder="Të gjitha (shkruaj emrin ose kodin)"
                                onChange={setSelectedActivities}
                                value={selectedActivities}
                            />
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Prej datës</label>
                            <input
                                type="date"
                                value={toIsoDate(selectedDateRange.start_date)}
                                max={toIsoDate(selectedDateRange.end_date)}
                                onChange={(e) =>
                                    setSelectedDateRange({
                                        ...selectedDateRange,
                                        start_date: e.target.value ? new Date(e.target.value) : null,
                                    })
                                }
                                className={inputClass}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Deri më datën</label>
                            <input
                                type="date"
                                value={toIsoDate(selectedDateRange.end_date)}
                                min={toIsoDate(selectedDateRange.start_date)}
                                onChange={(e) =>
                                    setSelectedDateRange({
                                        ...selectedDateRange,
                                        end_date: e.target.value ? new Date(e.target.value) : null,
                                    })
                                }
                                className={inputClass}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/60">
                    <button
                        type="button"
                        onClick={resetFilters}
                        className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                    >
                        Reset
                    </button>
                    <div className="inline-flex items-center gap-2">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                        >
                            Anulo
                        </button>
                        <button
                            type="button"
                            onClick={applyFilters}
                            className="inline-flex items-center gap-2 rounded-xl bg-secondary hover:bg-secondary/90 active:scale-[0.98] px-4 py-2 text-sm font-semibold text-white shadow-sm transition"
                        >
                            <FiSearch size={16} />
                            Apliko filterat
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
